import React, { useState, useEffect, useRef } from 'react';

// --- Helper Data ---
const servicesData = [
    {
        title: "Modular Kitchens",
        description: "Sleek, functional, and stylish kitchens for the modern home chef.",
        imgSrc: [
            "/Kitchen-Img/Kitchen-Img1.jpeg",
            "/Kitchen-Img/Kitchen-Img2.jpeg",
            "/Kitchen-Img/Kitchen-Img3.png",
            "/Kitchen-Img/Kitchen-Img4.png"
        ]
    },
    {
        title: "Living Rooms",
        description: "Comfortable and elegant living spaces for relaxing and entertaining.",
        imgSrc: [
            "/Living-Room-Img/LivingRoom-Img1.png",
            "/Living-Room-Img/LivingRoom-Img2.png",
            "/Living-Room-Img/LivingRoom-Img3.jpeg",
            "/Living-Room-Img/LivingRoom-Img4.jpeg"
        ]
    },
    {
        title: "Wardrobe Designs",
        description: "Custom wardrobes that maximize storage and complement your bedroom's aesthetic.",
        imgSrc: [
            "/Wardrobe-Designs-Img/Wardrobe-Img1.jpeg",
            "/Wardrobe-Designs-Img/Wardrobe-Img2.jpeg",
            "/Wardrobe-Designs-Img/Wardrobe-Img3.jpeg",
            "/Wardrobe-Designs-Img/Wardrobe-Img4.jpeg"
        ]
    },
    {
        title: "Full Home Interiors",
        description: "A complete home makeover with a unified design language for a seamless look.",
        imgSrc: [
            "/Full-Home-Interiors-Img/FullHomeInterior-Img1.jpeg",
            "/Full-Home-Interiors-Img/FullHomeInterior-Img2.jpeg",
            "/Full-Home-Interiors-Img/FullHomeInterior-Img3.jpeg",
            "/Full-Home-Interiors-Img/FullHomeInterior-Img4.png"
        ]
    },
    {
        title: "Bathroom Styling",
        description: "Transform your bathrooms into serene, spa-like retreats.",
        imgSrc: [
            "/Bathroom-Styling-Img/Bathroom-Img1.png",
            "/Bathroom-Styling-Img/Bathroom-Img2.jpeg",
            "/Bathroom-Styling-Img/Bathroom-Img3.jpeg",
            "/Bathroom-Styling-Img/Bathroom-Img4.png"
        ]
    },
    {
        title: "Commercial Spaces",
        description: "Inspiring and productive environments for offices and retail stores.",
        imgSrc: [
            "/Commercial-Spaces-Img/Commercial-Space-Img1.jpeg",
            "/Commercial-Spaces-Img/Commercial-Space-Img2.jpeg",
            "/Commercial-Spaces-Img/Commercial-Space-Img3.jpeg",
            "/Commercial-Spaces-Img/Commercial-Space-Img4.jpeg"
        ]
    }
];

const testimonialsData = [
    {
        quote: "AKThinkers completely transformed our office in Sector 62, Noida. Their sense of space optimization, clean aesthetics, and attention to acoustic detail has drastically improved our work environment.",
        name: "Rohan Mehta",
        location: "TechSphere Pvt. Ltd. (Noida)",
        avatar: "https://placehold.co/50x50/600/FFF?text=R"
    },
    {
        quote: "I wanted my 3BHK apartment in Greater Noida to have a warm, modern feel. AKThinkers delivered beyond expectations — from mood lighting to a stunning open kitchen concept. It’s functional, cozy, and beautiful!",
        name: "Priya Khurana, Software Engineer",
        location: "3 BHK, Greater Noida",
        avatar: "https://placehold.co/50x50/333/FFF?text=P"
    },
    {
        quote: "From the modular kitchen to the custom wardrobes, every element is flawless. The quality of work and the materials used are top-notch. Thank you, AKThinkers!",
        name: "The Reddy Family",
        location: "Villa, Bangalore",
        avatar: "https://placehold.co/50x50/900/FFF?text=S"
    }
];

const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#about", label: "About Us" },
    { href: "#services", label: "Services" },
    { href: "#estimator", label: "Price Estimator" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#contact", label: "Contact" }
];


// --- Custom Hooks ---

const useScrollFadeIn = () => {
    useEffect(() => {
        const fadeElems = document.querySelectorAll('.section-fade-in');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.1 });

        fadeElems.forEach(elem => observer.observe(elem));
        return () => fadeElems.forEach(elem => observer.unobserve(elem));
    }, []);
};

const useActiveSection = (sectionIds) => {
    const [activeSection, setActiveSection] = useState(sectionIds[0]);

    useEffect(() => {
        const handleScroll = () => {
            let current = sectionIds[0];
            for (const id of sectionIds) {
                const section = document.getElementById(id);
                if (section && section.getBoundingClientRect().top < window.innerHeight / 2) {
                    current = id;
                }
            }
            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sectionIds]);

    return activeSection;
};


// --- Components ---

const Header = ({ activeSection }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    return (
        <header className="bg-white/80 backdrop-blur-lg fixed top-0 left-0 right-0 z-50 shadow-sm transition-all duration-300">
            <div className="container mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
                <a href="#home" className="flex flex-col">
                    <span className="text-2xl font-bold font-serif text-purple-700 leading-none">AKThinkers</span>
                    <span className="text-[13.7px] text-gray-500 tracking-tighter">COA: CA/2020/126501</span>
                </a>
                
                <nav className="hidden lg:flex items-center space-x-8">
                    {navLinks.map(link => (
                        <a 
                            key={link.href}
                            href={link.href} 
                            className={`nav-link text-gray-600 font-medium ${activeSection === link.href.substring(1) ? 'active-nav' : ''}`}
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center space-x-2">
                    {!isMenuOpen && (
                         <a href="#consultation" className="hidden sm:inline-block bg-purple-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300 shadow-lg shadow-purple-500/20 text-sm">Book Free Consultation</a>
                    )}
                    <button ref={buttonRef} onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
                    </button>
                </div>
            </div>

            {isMenuOpen && (
                <div ref={menuRef} className="lg:hidden px-6 pb-4 border-t border-gray-200 bg-white/95">
                    {navLinks.map(link => (
                        <a 
                            key={link.href}
                            href={link.href} 
                            onClick={() => setIsMenuOpen(false)}
                            className="block py-2 text-gray-600 font-medium"
                        >
                            {link.label}
                        </a>
                    ))}
                    <a href="#consultation" onClick={() => setIsMenuOpen(false)} className="block mt-2 text-center bg-purple-600 text-white font-semibold px-5 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300">Book Free Consultation</a>
                </div>
            )}
        </header>
    );
};

// --- REVISED HomeSection Component ---
const HomeSection = () => (
    <section id="home" className="relative min-h-screen flex items-center pt-20">
        
        {/* Desktop Background Image (hidden on small screens) */}
        <div 
            className="absolute inset-0 bg-cover bg-right-bottom hidden lg:block"
            style={{ backgroundImage: `url(/Homepage-Background-Img/homepage-background-desktop.jpeg)` }}
        ></div>

        {/* Mobile Background Image (visible only on small screens) */}
        <div 
            className="absolute inset-0 bg-cover bg-right-bottom block lg:hidden"
            style={{ backgroundImage: `url(/Homepage-Background-Img/homepage-background-mobile.jpg)` }}
        ></div>

        {/* Black Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>

        {/* Content */}
        <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white leading-tight mb-4">Crafting Spaces, Creating Dreams</h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">From concept to creation, we bring your vision to life with bespoke interior designs that reflect your personality and style.</p>
            <a href="#quote" className="bg-white text-purple-700 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-transform transform hover:scale-105 duration-300 shadow-2xl">Get a Free Quote</a>
        </div>
    </section>
);



const AboutUsSection = () => (
    <section id="about" className="py-20 md:py-28 bg-white section-fade-in">
        <div className="container mx-auto px-6">
            <div className="text-center mb-8 md:mb-16">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">About AKThinkers</h2>
                <p className="text-lg text-gray-600 mt-2 max-w-2xl mx-auto">Where creativity meets craftsmanship.</p>
            </div>
            <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                <div className="prose prose-lg text-gray-600 order-2 md:order-1 md:w-1/2">
                    <p>
                        Established in 2020, AKThinkers is a design and build firm dedicated to creating exceptional spaces. We provide integrated architecture and interior design solutions for both residential and commercial projects. Our philosophy is simple: to design thoughtful, functional, and aesthetically compelling environments that perfectly balance form and function. From the initial blueprint to the final finishing touch, we build spaces that inspire.
                    </p>
                </div>
                <div className="order-1 md:order-2 md:w-1/2">
                     <img src="/About-AKThinkers.jpeg" alt="Our Team" className="rounded-2xl shadow-xl w-full h-auto" />
                </div>
            </div>
        </div>
    </section>
);


const ServiceCard = ({ service }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % service.imgSrc.length);
        }, 3000); 

        return () => clearInterval(intervalId);
    }, [service.imgSrc.length]);

    return (
        <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 transform hover:-translate-y-2 flex flex-col">
            <div className="relative w-full h-56">
                {service.imgSrc.map((src, index) => (
                    <img
                        key={src}
                        src={src}
                        alt={`${service.title} example ${index + 1}`}
                        className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}
                    />
                ))}
            </div>
            <div className="p-6 flex-grow">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
            </div>
        </div>
    );
};

const ServicesSection = () => (
    <section id="services" className="py-20 md:py-28 section-fade-in" style={{backgroundColor: '#f8f7f5'}}>
        <div className="container mx-auto px-6">
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">Our Interior Solutions</h2>
                <p className="text-lg text-gray-600 mt-2 max-w-2xl mx-auto">We offer a complete range of services for all your interior needs.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {servicesData.map(service => (
                    <ServiceCard key={service.title} service={service} />
                ))}
            </div>
        </div>
    </section>
);

const homePropertyTypes = [
    { label: "1 BHK (approx 600 sqft)", base: 250000 },
    { label: "2 BHK (approx 1000 sqft)", base: 500000 },
    { label: "3 BHK (approx 1400 sqft)", base: 700000 },
    { label: "4 BHK / Villa (approx 2000 sqft)", base: 1200000 },
];

const commercialPropertyTypes = [
    { label: "Small Office (500 sqft)", base: 700000 },
    { label: "Medium Office (1000 sqft)", base: 1400000 },
    { label: "Large Office (2000 sqft)", base: 2800000 },
];

const homePackages = [
    { label: "Essentials", multiplier: 1 },
    { label: "Premium", multiplier: 1.63 },
    { label: "Luxe", multiplier: 2.48 },
];

const commercialPackages = [
    { label: "Essentials", multiplier: 1 }, // Base: 14,00,000 for 1000 sqft
    { label: "Premium", multiplier: 1.43 }, // 20,00,000 / 14,00,000
    { label: "Luxe", multiplier: 2.14 }, // 30,00,000 / 14,00,000
];

const PriceEstimatorSection = () => {
    const [estimateType, setEstimateType] = useState('Home'); // 'Home' or 'Commercial'
    
    const [currentPropertyTypes, setCurrentPropertyTypes] = useState(homePropertyTypes);
    const [currentPackages, setCurrentPackages] = useState(homePackages);
    const [selectedProperty, setSelectedProperty] = useState(homePropertyTypes[1]);
    const [selectedPackage, setSelectedPackage] = useState(homePackages[1]);

    useEffect(() => {
        if (estimateType === 'Home') {
            setCurrentPropertyTypes(homePropertyTypes);
            setCurrentPackages(homePackages);
            setSelectedProperty(homePropertyTypes[1]); // Default to 2 BHK
            setSelectedPackage(homePackages[1]); // Default to Premium
        } else {
            setCurrentPropertyTypes(commercialPropertyTypes);
            setCurrentPackages(commercialPackages);
            setSelectedProperty(commercialPropertyTypes[1]); // Default to Medium Office
            setSelectedPackage(commercialPackages[0]); // Default to Essentials
        }
    }, [estimateType]);

    const estimatedPrice = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(selectedProperty.base * selectedPackage.multiplier);

    return (
        <section id="estimator" className="py-20 md:py-28 bg-white section-fade-in">
            <div className="container mx-auto px-6">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">Interior Price Estimator</h2>
                    <p className="text-lg text-gray-600 mt-2 max-w-2xl mx-auto">Get a quick estimate for your interiors. It's simple and fast!</p>
                </div>
                <div className="max-w-4xl mx-auto bg-white p-8 md:p-12 rounded-2xl shadow-xl">
                    <div className="flex justify-center mb-8">
                        <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-100 p-1">
                            <button onClick={() => setEstimateType('Home')} className={`font-semibold p-3 rounded-md transition-all ${estimateType === 'Home' ? 'bg-white shadow-sm' : 'bg-transparent'}`}>Home Interiors</button>
                            <button onClick={() => setEstimateType('Commercial')} className={`font-semibold p-3 rounded-md transition-all ${estimateType === 'Commercial' ? 'bg-white shadow-sm' : 'bg-transparent'}`}>Commercial</button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div className="space-y-6">
                            <div>
                                <label htmlFor="propertyType" className="block text-lg font-semibold text-gray-700 mb-2">1. Select Property Type</label>
                                <select 
                                    id="propertyType" 
                                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
                                    onChange={(e) => setSelectedProperty(currentPropertyTypes.find(p => p.label === e.target.value))}
                                    value={selectedProperty.label}
                                >
                                    {currentPropertyTypes.map(pt => <option key={pt.label} value={pt.label}>{pt.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-lg font-semibold text-gray-700 mb-2">2. Choose Your Package</label>
                                <div className="grid grid-cols-3 gap-2 rounded-lg bg-gray-100 p-1">
                                    {currentPackages.map(pkg => (
                                        <button 
                                            key={pkg.label}
                                            onClick={() => setSelectedPackage(pkg)}
                                            className={`estimator-btn font-semibold p-3 rounded-md transition-all ${selectedPackage.label === pkg.label ? 'bg-white shadow-sm' : 'bg-transparent'}`}
                                        >
                                            {pkg.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="text-center bg-purple-50 p-8 rounded-2xl border-2 border-dashed border-purple-200">
                            <p className="text-lg font-medium text-purple-800">Estimated Cost</p>
                            <p className="text-4xl md:text-5xl font-bold text-purple-700 my-2">{estimatedPrice.replace('₹', '₹ ')}</p>
                            <p className="text-sm text-gray-500">*This is an approximate cost. Final price may vary.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const TestimonialsSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonialsData.length);
        }, 5000);
        return () => clearTimeout(timer);
    }, [currentIndex]);

    return (
        <section id="testimonials" className="py-20 md:py-28 section-fade-in" style={{backgroundColor: '#f8f7f5'}}>
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900">What Our Clients Say</h2>
                    <p className="text-lg text-gray-600 mt-2">We are humbled by the praise from our amazing clients.</p>
                </div>
                <div className="relative max-w-3xl mx-auto h-80 md:h-64">
                    {testimonialsData.map((testimonial, index) => (
                        <div key={testimonial.name} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentIndex ? 'opacity-100' : 'opacity-0'}`}>
                            <div className="bg-white p-8 rounded-2xl shadow-lg h-full flex flex-col justify-center items-center text-center">
                                <p className="text-gray-600 text-lg italic mb-6">"{testimonial.quote}"</p>
                                <div className="flex items-center">
                                    <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full mr-4" />
                                    <div>
                                        <p className="font-bold text-gray-800">{testimonial.name}</p>
                                        <p className="text-sm text-gray-500">{testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                 <div className="flex justify-center mt-8 space-x-2">
                    {testimonialsData.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            className={`w-3 h-3 rounded-full transition-colors ${currentIndex === index ? 'bg-purple-600' : 'bg-gray-300'}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

const ConsultationSection = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleWhatsAppSubmit = (e) => {
        e.preventDefault();
        const businessWhatsAppNumber = '917982744336';
        const fullMessage = `New Quote Request:\n\n*Name:* ${name}\n*Email:* ${email}\n*Phone:* ${phone}\n\n*Message:*\n${message}`;
        const encodedMessage = encodeURIComponent(fullMessage);
        const whatsappURL = `https://wa.me/${businessWhatsAppNumber}?text=${encodedMessage}`;
        window.open(whatsappURL, '_blank');
        setIsSubmitted(true);
    };

    return (
        <section id="consultation" className="py-20 md:py-28 bg-purple-50 section-fade-in">
            <div className="container mx-auto px-6">
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
                    <div id="quote" className="w-full md:w-1/2 p-8 md:p-12 flex flex-col">
                        {isSubmitted ? (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Ready to Send!</h2>
                                <p className="text-gray-600">Your message is ready. Please press "Send" in WhatsApp to submit your request.</p>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Get a Free Quote</h2>
                                    <p className="text-gray-600 mb-6">Fill out the form and we'll connect on WhatsApp.</p>
                                </div>
                                <div className="md:hidden w-full bg-cover bg-center rounded-lg mb-6" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?q=80&w=2071&auto=format&fit=crop')", minHeight: '200px' }}>
                                </div>
                                <form onSubmit={handleWhatsAppSubmit} className="flex-grow flex flex-col">
                                    <div className="space-y-4 flex-grow flex flex-col">
                                        <input 
                                            type="text" 
                                            value={name} 
                                            onChange={(e) => setName(e.target.value)} 
                                            placeholder="Your Name" 
                                            required 
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" 
                                        />
                                        <input 
                                            type="email" 
                                            value={email} 
                                            onChange={(e) => setEmail(e.target.value)} 
                                            placeholder="Your Email" 
                                            required 
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" 
                                        />
                                        <input 
                                            type="tel" 
                                            value={phone} 
                                            onChange={(e) => setPhone(e.target.value)} 
                                            placeholder="Your Phone Number" 
                                            required 
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" 
                                        />
                                        <textarea 
                                            value={message} 
                                            onChange={(e) => setMessage(e.target.value)} 
                                            placeholder="Tell us about your requirements..." 
                                            rows="4" 
                                            required 
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" 
                                        />
                                        <div className="flex-grow"></div>
                                        <button type="submit" className="w-full bg-purple-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors duration-300 shadow-lg shadow-purple-500/30">
                                            Submit Request via WhatsApp
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                    <div className="hidden md:block w-full md:w-1/2 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?q=80&w=2071&auto=format&fit=crop')", minHeight: '300px' }}>
                    </div>
                </div>
            </div>
        </section>
    );
};

const WhatsAppButton = () => (
     <a href="https://wa.me/917982744336?text=Hello!%20I'm%20interested%20in%20your%20interior%20design%20services." target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 bg-green-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-xl hover:bg-green-600 transition-all transform hover:scale-110">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></svg>
    </a>
);

const Footer = () => (
    <footer id="contact" className="bg-gray-900 text-white pt-16 pb-8">
        <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
                <div>
                    <h3 className="text-xl font-bold font-serif mb-4">AKThinkers</h3>
                    <p className="text-gray-400">Designing beautiful homes since 2015. Your vision, our expertise.</p>
                </div>
                <div>
                    <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                    <ul className="space-y-2">
                        {navLinks.slice(1).map(link => (
                            <li key={link.href}><a href={link.href} className="text-gray-400 hover:text-white">{link.label}</a></li>
                        ))}
                         <li><a href="#consultation" className="text-gray-400 hover:text-white">Book Consultation</a></li>
                    </ul>
                </div>
                <div>
                    <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
                    <p className="text-gray-400">F-2, Gyc Galleria , Gaur Yamuna City, Sec-19, Greater Noida, Uttar Pradesh, India</p> <br></br>
                    <p className="text-gray-400">A-1804, Apex Alphabet, Sector-1, Greater Noida, Uttar Pradesh, India</p> <br></br>
                    <p className="text-gray-400">1st Floor, Sector 15, Commercial Market, Gurugram, Haryana, India</p><br></br>
                    <p className="text-gray-400">Email: contact@akthinkers.com</p>
                    <p className="text-gray-400">Phone: +91 7982744336</p>
                </div>
            </div>
            <div className="mt-12 border-t border-gray-800 pt-6 text-center text-gray-500 text-sm">
                <p>&copy; 2024 AKThinkers. All Rights Reserved.</p>
            </div>
        </div>
    </footer>
);


// --- Main App Component ---
export default function App() {
    useScrollFadeIn();
    
    const sectionIds = navLinks.map(link => link.href.substring(1));
    const activeSection = useActiveSection(sectionIds);

    return (
        <div className="text-gray-800">
            <Header activeSection={activeSection} />
            <main>
                <HomeSection />
                <AboutUsSection />
                <ServicesSection />
                <PriceEstimatorSection />
                <TestimonialsSection />
                <ConsultationSection />
            </main>
            <Footer />
            <WhatsAppButton />
        </div>
    );
}
